import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, ExternalLink, Download, ShoppingBag, BookOpen, Utensils, ChefHat, Users, DollarSign, ClipboardList, ShieldCheck, HelpCircle } from 'lucide-react';
import { Section, SectionTitle, AnimatedCard } from '@/components/Section';
import { Button } from '@/components/ui/button';

const ETSY_SHOP = 'https://www.etsy.com/shop/ChefBBsKitchen';

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.1 },
  transition: { duration: 0.45 },
};

const products = [
  {
    id: 'market-booth-kit',
    title: 'Farmers Market Food Booth Planner | Excel Cost & Profit Spreadsheet + PDF Workbook',
    shortTitle: 'Market Booth Planner',
    audience: 'For aspiring food vendors and market booth operators',
    description: 'Before you set up the tent, work through the numbers. This kit combines a planning spreadsheet with a practical workbook to help you estimate menu costs, sales, operating expenses, and potential profit.',
    highlights: [
      'Plan a menu and estimate ingredient costs.',
      'Explore daily sales and operating-cost scenarios.',
      'Consider unsold food and waste.',
      'Prepare for practical booth concerns, including local health requirements, safety equipment, and securing a tent in windy conditions.',
    ],
    note: 'A planning and estimation resource. Check requirements with your local authorities before operating.',
    icon: ShoppingBag,
    image: '/assets/chefbb-market-booth.jpg',
    link: 'https://www.etsy.com/listing/4590535197/farmers-market-food-booth-planner-excel',
    linkLabel: 'View the Market Booth Kit on Etsy',
  },
  {
    id: 'restaurant-guide',
    title: 'The Beginner\'s Guide to Restaurant Success',
    shortTitle: 'Restaurant Success Guide',
    audience: 'For people considering their first restaurant',
    description: 'Opening a restaurant takes more than a good menu. This guide introduces the business side of restaurant ownership and helps aspiring operators think through the responsibilities and decisions that come with serving the public.',
    supporting: "Learn from Chef BB's experience before committing your time, money, and energy.",
    icon: BookOpen,
    image: '/assets/chefbb-restaurant-guide.png',
    link: 'https://www.etsy.com/listing/4590594877/the-beginners-guide-to-restaurant',
    linkLabel: 'View the Restaurant Guide on Etsy',
  },
  {
    id: 'use-what-you-got',
    title: 'Use What You Got: Entrepreneurship & Personal Growth PDF',
    shortTitle: 'Use What You Got',
    audience: 'For resourceful entrepreneurs and people ready to act on an idea',
    description: 'Start with the skills, experience, and resources already within reach. This book introduces the philosophy behind Chef BB\'s work: recognize what you have, find a useful way to put it to work, and take practical steps forward.',
    supporting: 'For readers who want to turn resourcefulness into action.',
    icon: Utensils,
    image: '/assets/chefbb-uwyg-book.jpg',
    link: 'https://www.etsy.com/listing/4590609705/use-what-you-got-and-make-a-lot',
    linkLabel: 'View Use What You Got on Etsy',
  },
];

const faqs = [
  {
    q: 'Is Chef BB the same person as R. Dale Brunet?',
    a: 'Yes. Chef BB is Robert Dale Brunet\'s culinary persona. R. Dale Brunet is the name he uses for his work as an author and publisher.',
  },
  {
    q: 'Where can I buy these products?',
    a: 'The current collection is available through Chef BB\'s Kitchen on Etsy. Each listing explains what is included and how the digital files are delivered.',
  },
  {
    q: 'Are these physical products?',
    a: 'The products featured here are digital purchases through Etsy. Check the individual listing for the included files and formats.',
  },
  {
    q: 'Does the Market Booth Kit replace local licensing guidance?',
    a: 'No. It helps you plan and estimate. Health, licensing, and operating requirements vary by location, so confirm them with the relevant local authorities.',
  },
];

// ── HERO ──
function HeroSection() {
  return (
    <Section dark className="pt-24 md:pt-28">
      <div className="grid lg:grid-cols-[1fr_0.7fr] gap-10 lg:gap-16 items-center">
        <motion.div {...fade}>
          <div className="flex items-center gap-4 mb-6">
            <img
              src="/assets/chef-bb-logo.png"
              alt="Chef BB logo"
              className="w-14 h-14 rounded-full object-contain bg-white p-1"
              data-testid="chefbb-logo"
            />
            <span className="text-sm text-primary font-medium uppercase tracking-widest" data-testid="chefbb-eyebrow">
              Chef BB &bull; R. Dale Brunet
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mb-4 leading-tight" data-testid="chefbb-h1">
            Chef BB: Books and Tools for Food Business Success
          </h1>
          <p className="text-lg md:text-xl text-primary/90 font-medium mb-6">
            Recipes for Success in Your Kitchen and Your Business
          </p>
          <p className="text-base text-slate-300 leading-relaxed mb-8 max-w-xl">
            Chef BB is the name people came to know in my kitchens. I'm Robert Dale Brunet, writing and publishing as R. Dale Brunet. The lessons I learned as a private chef, restaurant owner, entrepreneur, and dishwasher now inform the books and practical tools I create for people ready to put their ideas to work.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              asChild size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="chefbb-explore-btn"
            >
              <a href="#products">Explore the Books & Tools</a>
            </Button>
            <Button
              asChild size="lg" variant="outline"
              className="border-primary text-primary hover:bg-primary/10"
              data-testid="chefbb-etsy-btn"
            >
              <a href={ETSY_SHOP} target="_blank" rel="noopener noreferrer">
                Shop Chef BB's Kitchen on Etsy <ExternalLink className="ml-2 w-4 h-4" />
              </a>
            </Button>
          </div>
        </motion.div>

        <motion.div
          className="flex justify-center"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <img
            src="/assets/chef-bb-kitchen.webp"
            alt="Chef BB — Robert Dale Brunet cooking in the kitchen"
            className="w-full max-w-md rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.4)] object-cover aspect-[3/4]"
            data-testid="chefbb-hero-image"
          />
        </motion.div>
      </div>
    </Section>
  );
}

// ── EXPERIENCE ──
function ExperienceSection() {
  const milestones = [
    { year: 'Roots', text: 'Grew up in the New Orleans area — 10th of 13 siblings, nicknamed "baby brother" (BB)' },
    { year: '2010', text: 'Began private chef work through House Chef of Colorado Springs' },
    { year: '2013', text: "Opened Momma Pearl's Cajun Kitchen (August)" },
    { year: '2024', text: "Momma Pearl's operated until June 2024" },
    { year: 'Now', text: 'Develops books, practical tools, and resources as an author, publisher, and entrepreneur' },
  ];

  return (
    <Section>
      <div className="max-w-4xl mx-auto">
        <SectionTitle subtitle="The Story">From Louisiana Roots to Colorado Kitchens</SectionTitle>
        <motion.div className="space-y-5 text-slate-300 text-base leading-relaxed mb-10" {...fade}>
          <p>
            The name Chef BB grew from "baby brother" — a family nickname for the tenth of thirteen children. My Louisiana upbringing helped shape the food I cooked and the hospitality I brought to the table.
          </p>
          <p>
            In 2010, I began working as a private chef through House Chef of Colorado Springs. In 2013, I opened Momma Pearl's Cajun Kitchen, which operated until June 2024. Those years taught me about much more than recipes: ingredient costs, staffing, customer expectations, daily decisions, and the work that continues after the last plate leaves the kitchen.
          </p>
        </motion.div>

        {/* Timeline */}
        <motion.div className="mb-10" {...fade}>
          <div className="relative border-l-2 border-primary/30 ml-4 pl-8 space-y-6" role="list" aria-label="Career milestones">
            {milestones.map((m) => (
              <div key={m.year} className="relative" role="listitem">
                <div className="absolute -left-[2.55rem] top-1 w-3 h-3 rounded-full bg-primary border-2 border-background" />
                <p className="text-xs text-primary font-semibold uppercase tracking-wider mb-1">{m.year}</p>
                <p className="text-sm text-slate-300">{m.text}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="bg-card border border-border/30 rounded-xl p-6 md:p-8"
          {...fade}
        >
          <p className="text-base text-slate-300 leading-relaxed">
            Chef BB belongs here because he is part of the experience behind R. Dale Brunet's work. Today, I turn those lessons into books and tools that help people think through a business idea, understand their numbers, and make better use of the resources they have.
          </p>
          <Link
            to="/about"
            className="text-primary text-sm font-medium inline-flex items-center gap-1 mt-4 hover:gap-2 transition-all"
          >
            About R. Dale Brunet <ChevronRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </Section>
  );
}

// ── PRODUCTS ──
function ProductsSection() {
  return (
    <Section dark id="products">
      <SectionTitle subtitle="Available Now">Chef BB's Books & Practical Tools</SectionTitle>
      <motion.p className="text-slate-400 mb-8 max-w-2xl -mt-4 text-base" {...fade}>
        Whether you're planning a food booth, considering a restaurant, or looking for a practical way to move an idea forward, start with the resource that fits your next step.
      </motion.p>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {products.map((p, i) => (
          <AnimatedCard key={p.id} delay={i * 0.1} className="flex flex-col">
            {/* Product artwork */}
            {p.image && (
              <div className="h-44 -mx-6 -mt-6 md:-mx-8 md:-mt-8 mb-5 bg-slate-900 overflow-hidden rounded-t-xl">
                <img
                  src={p.image}
                  alt={p.shortTitle || p.title}
                  className="w-full h-full object-contain p-2"
                  loading="lazy"
                />
              </div>
            )}

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <p.icon className="w-5 h-5 text-primary" />
              </div>
              <span className="text-xs text-primary font-semibold uppercase tracking-wider">{p.audience}</span>
            </div>

            <h3 className="text-lg font-bold text-white mb-3">{p.title}</h3>
            <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{p.description}</p>

            {p.highlights && (
              <ul className="space-y-2 mb-4" role="list">
                {p.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-sm text-slate-400">
                    <ClipboardList className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            )}

            {p.note && (
              <p className="text-xs text-slate-500 italic mb-4 flex items-start gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                {p.note}
              </p>
            )}

            {p.supporting && (
              <p className="text-sm text-slate-400 italic mb-4">{p.supporting}</p>
            )}

            <div className="mt-auto pt-4">
              <a
                href={p.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary text-sm font-semibold hover:gap-3 transition-all"
                data-testid={`chefbb-product-${p.id}`}
              >
                {p.linkLabel} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </AnimatedCard>
        ))}
      </div>

      <motion.p className="text-xs text-slate-500 text-center" {...fade}>
        These products are available as digital purchases through Etsy. Visit each listing for current pricing, included files, and download details.
      </motion.p>
    </Section>
  );
}

// ── PHILOSOPHY ──
function PhilosophySection() {
  return (
    <Section>
      <div className="max-w-3xl mx-auto text-center">
        <motion.div {...fade}>
          <span className="text-sm text-primary font-medium uppercase tracking-widest mb-4 block">Philosophy</span>
          <blockquote className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-6 leading-snug" data-testid="chefbb-motto">
            Use What You Got and Make a Lot
          </blockquote>
          <div className="w-12 h-1 bg-primary mx-auto mb-6 rounded-full" />
          <p className="text-base text-slate-300 leading-relaxed mb-4">
            That idea has followed me through technology, kitchens, restaurant ownership, and publishing. Start by understanding what you have. Learn what you need. Work through the numbers. Then take a step you can build on.
          </p>
          <p className="text-base text-slate-400 leading-relaxed">
            The books and tools here are designed to help with that process — bringing practical experience to the decisions in front of you.
          </p>
        </motion.div>
      </div>
    </Section>
  );
}

// ── FAQ ──
function FAQSection() {
  return (
    <Section dark id="faq">
      <div className="max-w-3xl mx-auto">
        <SectionTitle subtitle="Questions">Frequently Asked</SectionTitle>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <motion.details
              key={faq.q}
              className="group bg-card border border-border/30 rounded-xl overflow-hidden"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.08 }}
            >
              <summary
                className="flex items-start gap-3 p-5 cursor-pointer list-none text-white font-medium hover:text-primary transition-colors [&::-webkit-details-marker]:hidden"
                data-testid={`chefbb-faq-${i}`}
              >
                <HelpCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span>{faq.q}</span>
                <ChevronRight className="w-4 h-4 ml-auto mt-0.5 flex-shrink-0 text-slate-500 transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-5 pb-5 pl-13 text-sm text-slate-400 leading-relaxed">
                {faq.a}
              </div>
            </motion.details>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ── CLOSING CTA ──
function ClosingCTA() {
  return (
    <Section className="pb-16 md:pb-20">
      <div className="max-w-2xl mx-auto text-center">
        <motion.div {...fade}>
          <span className="text-sm text-primary font-medium uppercase tracking-widest mb-4 block">Get Started</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
            Put Your Next Idea to Work
          </h2>
          <p className="text-base text-slate-400 mb-8">
            Explore Chef BB's books and tools, and choose the resource that fits your next step.
          </p>
          <Button
            asChild size="lg"
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="chefbb-closing-cta"
          >
            <a href={ETSY_SHOP} target="_blank" rel="noopener noreferrer">
              Visit Chef BB's Kitchen on Etsy <ExternalLink className="ml-2 w-4 h-4" />
            </a>
          </Button>
        </motion.div>
      </div>
    </Section>
  );
}

// ── JSON-LD ──
function StructuredData() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.rdbrunet.com/' },
          { '@type': 'ListItem', position: 2, name: 'Chef BB', item: 'https://www.rdbrunet.com/ChefBB' },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: 'Chef BB | Food Business Books & Tools | R. Dale Brunet',
        description: "Meet Chef BB, R. Dale Brunet's culinary persona. Explore food business planning tools, a restaurant startup guide, and practical books available on Etsy.",
        url: 'https://www.rdbrunet.com/ChefBB',
        isPartOf: { '@type': 'WebSite', url: 'https://www.rdbrunet.com/' },
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: 3,
          itemListElement: products.map((p, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: p.title,
            url: ETSY_SHOP,
          })),
        },
      },
      {
        '@type': 'Person',
        name: 'Robert Dale Brunet',
        alternateName: ['R. Dale Brunet', 'Chef BB'],
        description: 'Author, Publisher, AI Consultant, Executive Chef and Dishwasher.',
        sameAs: [ETSY_SHOP],
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

// ── MAIN ──
export default function ChefBBPage() {
  useEffect(() => {
    document.title = 'Chef BB | Food Business Books & Tools | R. Dale Brunet';
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(name.startsWith('og:') || name.startsWith('twitter:') ? 'property' : 'name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', "Meet Chef BB, R. Dale Brunet's culinary persona. Explore food business planning tools, a restaurant startup guide, and practical books available on Etsy.");
    setMeta('og:title', 'Chef BB | Food Business Books & Tools | R. Dale Brunet');
    setMeta('og:description', "Meet Chef BB, R. Dale Brunet's culinary persona. Explore food business planning tools, a restaurant startup guide, and practical books available on Etsy.");
    setMeta('og:url', 'https://www.rdbrunet.com/ChefBB');
    setMeta('og:type', 'website');
    setMeta('og:image', 'https://www.rdbrunet.com/assets/chef-bb-kitchen.webp');
    setMeta('og:image:alt', "Chef BB — R. Dale Brunet cooking in the kitchen");
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'Chef BB | Food Business Books & Tools | R. Dale Brunet');
    setMeta('twitter:description', "Meet Chef BB, R. Dale Brunet's culinary persona. Explore food business planning tools, a restaurant startup guide, and practical books available on Etsy.");
    setMeta('twitter:image', 'https://www.rdbrunet.com/assets/chef-bb-kitchen.webp');

    // Canonical
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement('link'); canon.setAttribute('rel', 'canonical'); document.head.appendChild(canon); }
    canon.setAttribute('href', 'https://www.rdbrunet.com/ChefBB');

    return () => {
      document.title = 'R. Dale Brunet | Technology Architect, AI Strategist, Founder, Author';
      canon?.remove();
      ['og:title','og:description','og:url','og:type','og:image','og:image:alt','twitter:card','twitter:title','twitter:description','twitter:image'].forEach(n => {
        const el = document.querySelector(`meta[property="${n}"]`) || document.querySelector(`meta[name="${n}"]`);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <main data-testid="chefbb-page">
      <StructuredData />
      <HeroSection />
      <ExperienceSection />
      <ProductsSection />
      <PhilosophySection />
      <FAQSection />
      <ClosingCTA />
    </main>
  );
}
